<script setup lang="ts">
import {computed, onBeforeMount, ref, watch} from "vue";
import {type FilterDisplayParams} from "ag-grid-community";
import {QSelect} from "quasar";
import Translator from "@/utils/common/Translator.ts";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import ReferenceWidget from "@/components/EditView/widgets/ReferenceWidget.vue";
import FieldType from "@/utils/constants/FieldType.ts";
import GenericReferenceWidget from "@/components/EditView/widgets/GenericReferenceWidget.vue";
import FieldMetaData = namaTypes.FieldMetaData;

const props = defineProps<{ params: FilterDisplayParams }>();
const columnMetaData = props.params['metaData'] as FieldMetaData;
const refQSelect = ref<QSelect>();
const entityTypeQSelect = ref<QSelect>();
let equalOption = {label: Translator.translateId('Equals'), value: 'equals'};
const selectedOption = ref<Record<string, any>>(equalOption);
const filterOptions = ref<Record<string, any>[]>([
  equalOption,
  {label: Translator.translateId('NotEqual'), value: 'notEqual'},
  {label: Translator.translateId('blank'), value: 'blank'},
  {label: Translator.translateId('notBlank'), value: 'notBlank'},
]);
const isRefVisible = computed(() => !['blank', 'notBlank'].contains(selectedOption.value.value));
let questionFieldPresenter = new QuestionFieldsPresenter();
let referenceQuestionField = NamaCommon.createQuestionFieldRef("reference", columnMetaData.referencedEntityType!);
let targetComponent = ReferenceWidget;
if (columnMetaData.fieldType === FieldType.Genericreference) {
  referenceQuestionField = NamaCommon.createQuestionFieldWithAllowedValues("reference", FieldType.Genericreference
    , columnMetaData.allowedValues ?? []);
  targetComponent = GenericReferenceWidget;
}
const reference = computed(() => questionFieldPresenter.record.value["reference"]);

onBeforeMount(async () => {
  await questionFieldPresenter.edit([referenceQuestionField], []);
  if (props.params.model) {
    const model = props.params.model;
    questionFieldPresenter.record.value['reference'] = model?.ref
    selectedOption.value = filterOptions.value.find(l => l.value === model?.type) ?? equalOption;
  }
})
watch(() => questionFieldPresenter.record, updateFilterModel, {deep: true});

function onUpdateOption(newValue: Record<string, any>) {
  selectedOption.value = newValue;
  updateFilterModel();
}

function buildModel(): Record<string, any> | null {
  const isFilterActive = (columnMetaData.fieldId && reference.value && (reference.value.entityType || reference.value.id)) || ['blank', 'notBlank'].contains(selectedOption.value?.value);
  if (!isFilterActive)
    return null;
  return {
    ref: reference.value,
    type: selectedOption.value.value,
    filterType: "reference"
  };
}

function updateFilterModel() {
  props.params.onModelChange(buildModel());
}

function restoreFromModel(model: Record<string, any> | null) {
  selectedOption.value = model ? (filterOptions.value.find(l => l.value === model.type) ?? equalOption) : equalOption;
  questionFieldPresenter.record.value["reference"] = model?.ref ?? undefined;
}

function refresh(newParams: FilterDisplayParams) {
  if (newParams?.model)
    return;
  if (!buildModel())
    return;
  restoreFromModel(null);
}

defineExpose({refresh});
</script>

<template>
  <div class="tw-flex tw-flex-col tw-gap-2 tw-rounded tw-p-2">
    <q-select ref="entityTypeQSelect"
              outlined dense options-dense
              :model-value="selectedOption"
              @update:model-value="onUpdateOption"
              :options="filterOptions"
              class="nama-criteria-field-input nama-customize-list-filter-dense-input mobile:tw-w-full ag-custom-component-popup"
              input-class="tw-bg-transparent focus:tw-ring-0 focus:tw-shadow-none focus:tw-border-none dark:tw-text-white
              dark:tw-placeholder-gray-300 header-search-in-popup-input tw-pl-2"
              options-selected-class="tw-text-base tw-font-normal tw-border-b-black tw-bg-primary-light dark:tw-bg-primary-dark tw-text-white"
              popup-content-class="!tw-max-h-[30vh] navbar-search-popup dark:tw-bg-namaBackground-deep-dark dark:tw-text-white ag-custom-component-popup"
    />
    <component :is="targetComponent" ref="refQSelect" :presenter="questionFieldPresenter" :field="referenceQuestionField"
               :field-meta-data="referenceQuestionField.metaData!" is-inside-composite v-if="isRefVisible" :isGridColumnFilterField="true"/>
  </div>
</template>

<style scoped>
</style>
